import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, CheckCircle2, AlertTriangle, ShieldAlert, Mail, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const QUESTIONS = [
  {
    q: "When did your company last file its annual return or state report?",
    opts: [["On time this year", 0], ["I'm not sure", 1], ["It's late", 2], ["Missed for a year or more", 3]],
  },
  {
    q: "Are your corporate taxes (income, sales, payroll) filed and paid on time?",
    opts: [["Yes, always", 0], ["Usually, sometimes late", 1], ["We're behind", 2]],
  },
  {
    q: "Where are your minute book, resolutions and key corporate records?",
    opts: [["Organized and up to date", 0], ["Somewhere, but scattered", 1], ["I don't know", 2]],
  },
];

const level = (v) => (v >= 2 ? "high" : v === 1 ? "watch" : "clear");

const assess = ([filing, tax, records]) => [
  ["Missed Filings", level(Math.min(filing, 2)), "Annual returns and state filings can trigger penalties and dissolution."],
  ["Tax Problems", level(tax), "Late tax obligations create interest, penalties and compliance risk."],
  ["Lost Records", level(records), "Missing corporate records create governance and audit issues."],
  ["Revival Costs", filing === 3 ? "high" : filing === 2 ? "watch" : "clear", "Fixing a dissolved corporation is significantly more expensive than preventing the problem."],
];

const STYLE = {
  high: ["Applies to you", "text-red-300 bg-red-500/10 border-red-500/30", ShieldAlert],
  watch: ["Worth watching", "text-amber-300 bg-amber-500/10 border-amber-500/30", AlertTriangle],
  clear: ["Looks fine", "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", CheckCircle2],
};

const slug = (t) => t.toLowerCase().replace(/\s+/g, "-");

const Question = ({ step, onPick, onBack }) => {
  const { q, opts } = QUESTIONS[step];
  return (
    <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
      <h4 className="text-lg sm:text-xl font-semibold text-white" data-testid="risk-check-question">{q}</h4>
      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        {opts.map(([label, v], i) => (
          <button key={label} onClick={() => onPick(v)} data-testid={`risk-check-option-${step}-${i}`}
            className="group flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-left text-sm text-slate-200 transition-[border-color,background-color,transform] duration-200 hover:border-emerald-500/50 hover:bg-emerald-500/[0.07] active:scale-[0.99]">
            {label}<ArrowRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-400" />
          </button>
        ))}
      </div>
      {step > 0 && (
        <button onClick={onBack} className="mt-5 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="risk-check-back-btn">
          <ArrowLeft className="h-3.5 w-3.5" />Back
        </button>
      )}
    </motion.div>
  );
};

const EmailResults = ({ answers }) => {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/public/risk-check", { email, answers, marketing_opt_in: optIn });
      setStatus("sent");
    } catch (err) {
      setError(errMsg(err));
      setStatus("idle");
    }
  };
  if (status === "sent") {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] px-4 py-3 text-sm text-emerald-200" data-testid="risk-check-email-sent">
        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />Sent! Check {email} for your results and action plan.
      </motion.div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="risk-check-email-form">
      <div className="flex items-center gap-2 text-sm font-semibold text-white"><Mail className="h-4 w-4 text-emerald-400" />Email me my results and an action plan</div>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
          className="flex-1 rounded-lg border border-white/10 bg-[#0B0F19] px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-emerald-500/60" data-testid="risk-check-email-input" />
        <button type="submit" disabled={status === "sending"} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-emerald-100 disabled:opacity-60" data-testid="risk-check-email-submit">
          {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}Send my results
        </button>
      </div>
      <label className="mt-3 flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
        <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-0.5 accent-emerald-500" data-testid="risk-check-optin" />
        Also send me up to three short corporate health tips this week, one for each risk that applies. Unsubscribe anytime.
      </label>
      {error && <p className="mt-2 text-xs text-red-300" data-testid="risk-check-email-error">{error}</p>}
    </form>
  );
};

const Results = ({ answers, onRestart }) => {
  const res = assess(answers);
  const flagged = res.filter(([, l]) => l === "high").length;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="risk-check-results">
      <h4 className="text-lg sm:text-xl font-semibold text-white" data-testid="risk-check-summary">
        {flagged ? `${flagged} of 4 risks apply to your company right now.` : "No major risks flagged. Keep it that way."}
      </h4>
      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        {res.map(([title, l, text], i) => {
          const [tag, cls, Icon] = STYLE[l];
          return (
            <motion.div key={title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}
              className={`rounded-xl border p-4 ${l === "clear" ? "border-white/[0.07] bg-white/[0.02]" : "border-white/10 bg-white/[0.04]"}`} data-testid={`risk-check-result-${slug(title)}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-white">{title}</span>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`} data-testid={`risk-check-result-${slug(title)}-level`}><Icon className="h-3 w-3" />{tag}</span>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{text}</p>
            </motion.div>
          );
        })}
      </div>
      <EmailResults answers={answers} />
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Link to="/register" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-500" data-testid="risk-check-cta">
          Track these risks free for 14 days <ArrowRight className="h-4 w-4" />
        </Link>
        <button onClick={onRestart} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="risk-check-restart-btn">
          <RotateCcw className="h-3.5 w-3.5" />Retake
        </button>
      </div>
    </motion.div>
  );
};

export const RiskCheck = () => {
  const [answers, setAnswers] = useState([]);
  const step = answers.length;
  const done = step === QUESTIONS.length;
  return (
    <div className="mt-10 rounded-2xl border border-white/10 bg-[#0E1424] p-6 sm:p-8" data-testid="risk-check">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-400">Free risk check</div>
          <p className="mt-1 text-sm text-slate-400">Three questions. See which risks apply to your company.</p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="risk-check-progress">
          {QUESTIONS.map((_, i) => <span key={i} className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${i < step ? "bg-emerald-500" : i === step ? "bg-white/40" : "bg-white/10"}`} />)}
          <span className="ml-2 font-mono text-xs text-slate-500">{done ? "Done" : `${step + 1}/3`}</span>
        </div>
      </div>
      <AnimatePresence mode="wait">
        {done
          ? <Results key="r" answers={answers} onRestart={() => setAnswers([])} />
          : <Question key={step} step={step} onPick={(v) => setAnswers([...answers, v])} onBack={() => setAnswers(answers.slice(0, -1))} />}
      </AnimatePresence>
    </div>
  );
};
